<template>
  <div class="upload-item-wrapper" :class="{ selected }">
    <div class="upload-item-inner-wrapper">
      <attachment-thumbnail
        class="attachment-thumbnail"
        :thumbnail-url="thumbUrl"
        :error="uploadError"
        :file-type-extension="fileExtension"
        :progress="uploadProgress / 100"
        data-drag-handle
        :style="{
          cursor: showGrab ? 'grab' : 'default',
        }"
      ></attachment-thumbnail>

      <TextBody
        style="flex-shrink: 1; word-break: break-word"
        @click="startEditing"
        v-if="!isEditing"
        :looks-editable="editable && !noRenaming"
        large
        :class="{ 'mr-12': !editable, 'mr-6': editable }"
        class="ml-6 pointer"
        >{{ uploadName }}
      </TextBody>
      <div v-if="isEditing" class="d-flex">
        <v-text-field
          :rules="[nameRule]"
          hide-details="true"
          class="ml-6 mr-0 pt-0"
          style="font-size: 18px"
          v-model="uploadName"
          @click.stop
          variant="underlined"
          autofocus
        ></v-text-field>
        <v-icon
          v-tooltip="{ content: 'Speichern', offset: 10 }"
          @click="emitEditAndStopEditing"
          class="pointer"
          >{{ mdiCheck }}
        </v-icon>
        <v-icon
          v-tooltip="{ content: 'Abbrechen', offset: 10 }"
          @click="closeEdit"
          class="pointer"
          >{{ mdiClose }}
        </v-icon>
      </div>
    </div>
    <v-menu offset-y v-if="editable">
      <template v-slot:activator="{ on, attrs }">
        <v-icon v-bind="attrs" v-on="on">
          {{ mdiDotsVertical }}
        </v-icon>
      </template>
      <v-list class="pointer">
        <v-list-item-group>
          <v-list-item v-if="uploadError && uploadProgress !== undefined">
            <v-list-item-title @click="$emit('retry')">
              {{ t$(".retry") }}
            </v-list-item-title>
          </v-list-item>
          <v-list-item v-if="!uploadError">
            <v-list-item-title @click="isEditing = true"
              >{{ t$(".rename") }}
            </v-list-item-title>
          </v-list-item>
          <v-list-item>
            <v-list-item-title @click="handleDelete">
              {{ t$(".delete") }}
            </v-list-item-title>
          </v-list-item>
          <template v-if="allowImageMode">
            <v-divider></v-divider>
            <v-tooltip :disabled="imageModeBlockedMessage.length === 0" bottom>
              <template v-slot:activator="{ on, attrs }">
                <div v-bind="attrs" v-on="on">
                  <v-list-item :disabled="imageModeBlockedMessage.length > 0">
                    <v-list-item-title @click="$emit('showAsImage')">
                      Als Bild anzeigen
                    </v-list-item-title>
                  </v-list-item>
                </div>
              </template>
              <span>{{ imageModeBlockedMessage }}</span>
            </v-tooltip>
          </template>
        </v-list-item-group>
      </v-list>
    </v-menu>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, watch } from "vue"
import AttachmentThumbnail from "./AttachmentThumbnail.vue"
import TextBody from "components/UI/TextBody.vue"
import { mdiDotsVertical, mdiClose, mdiCheck } from "@mdi/js"
import useTranslate from "features/common/translate/concerns/useTranslate"
import { useConfirmationService } from "features/common/confirmation-dialog/confirmation.service"

export default defineComponent({
  name: "AttachmentUploadTableItem",
  components: { TextBody, AttachmentThumbnail },
  props: {
    name: { type: String, default: "unnamed" },
    fileExtension: { type: String, default: "FILE" },
    thumbUrl: { type: String },
    uploadError: { type: String },
    uploadProgress: {
      type: Number,
      validator(value) {
        // The value must match one of these strings
        return value === undefined || value >= 0 || value <= 100
      },
    },
    selected: { type: Boolean, default: false },
    editable: { type: Boolean, default: false },
    allowImageMode: { type: Boolean, default: false },
    imageModeBlockedMessage: { type: String, default: "" },
    noRenaming: { type: Boolean, default: false },
    showGrab: { type: Boolean, default: false },
  },
  setup(props, { emit }) {
    const { t$ } = useTranslate("components.attachment_upload_item")
    const uploadName = ref(props.name)

    const isEditing = ref(false)

    const emitEditAndStopEditing = () => {
      if (!uploadName.value) {
        closeEdit()
        return
      }
      emit("uploadName", uploadName.value)
      isEditing.value = false
    }

    const startEditing = () => {
      if (props.editable && !props.noRenaming) {
        isEditing.value = true
      }
    }

    const closeEdit = () => {
      isEditing.value = false
      uploadName.value = props.name
    }
    const nameRule = (v) => !!v || "Name wird benötigt."
    watch(isEditing, (v) => emit("editing", v))

    const confirmationService = useConfirmationService()
    const handleDelete = () => {
      confirmationService.open({
        title: "Datei löschen",
        text: "Möchtest du die Datei wirklich löschen?",
        confirmText: "Löschen",
        onConfirm: () => emit("delete"),
      })
    }

    watch(
      () => isEditing.value,
      (v) => {
        if (v) {
          const selectedNode = document.querySelector(
            ".ProseMirror-selectednode"
          )
          if (selectedNode) {
            selectedNode.removeAttribute("draggable")
          }
        } else {
          const selectedNode = document.querySelector(
            ".ProseMirror-selectednode"
          )
          if (selectedNode) {
            selectedNode.setAttribute("draggable", "true")
          }
        }
      }
    )

    return {
      mdiDotsVertical,
      mdiClose,
      mdiCheck,
      t$,
      nameRule,
      uploadName,
      isEditing,
      closeEdit,
      startEditing,
      emitEditAndStopEditing,
      handleDelete,
    }
  },
})
</script>

<style scoped>
.upload-item-wrapper {
  flex-basis: 350px;
  background: #f2f2f2;
  border: 2px solid transparent;
  border-radius: 4px;
  overflow: hidden;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  max-width: 350px;

  &.selected {
    border: 2px solid #307ef7;
  }
}

.upload-item-inner-wrapper {
  display: flex;
  flex-direction: row;
  align-items: center;
}

.attachment-thumbnail {
  flex-shrink: 0;
}

.pointer {
  cursor: pointer;
}
</style>
